import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { getTasks, createTask, updateTask, deleteTask, getMembers } from './businessApi';
import Portal from '../../shared/Portal';

// ─── Energy Coach ─────────────────────────────────────────────────────────────

const MENTAL_BASE  = { critical: 35, high: 25, medium: 15, low: 8, none: 5 };
const PHYS_BASE    = { critical: 20, high: 15, medium: 10, low: 5, none: 3 };
const MENTAL_KW    = ['analys','design','ontwerp','plan','rapport','report','review','research','onderzoek','presentat','meeting','vergadering','deadline','urgent','strateg','complex','besliss','beoordel','voorstel','proposal','evaluate','evaluat','brainstorm'];
const PHYS_KW      = ['install','bouwen','build','carry','draag','move','verplaats','repair','repareer','fix','transport','ophalen','leveren','deliver','opbouw','afbraak','klus','montage','assemble','laden','unload'];

function calcEnergy(task, allTasks) {
  if (!task.assigned_to) return null;
  const desc = ((task.title || '') + ' ' + (task.description || '')).toLowerCase();

  const mentalHits = MENTAL_KW.filter(k => desc.includes(k)).length;
  const physHits   = PHYS_KW.filter(k => desc.includes(k)).length;

  let deadlinePressure = 0;
  if (task.due_date) {
    const days = Math.floor((new Date(task.due_date) - new Date()) / 86400000);
    if (days < 0)       deadlinePressure = 20;
    else if (days <= 1) deadlinePressure = 15;
    else if (days <= 3) deadlinePressure = 10;
    else if (days <= 7) deadlinePressure = 5;
  }

  const mentalCost = Math.min(95, (MENTAL_BASE[task.priority] || 5) + mentalHits * 5 + deadlinePressure);
  const physCost   = Math.min(95, (PHYS_BASE[task.priority]   || 3) + physHits  * 8);

  const peers = allTasks.filter(t =>
    t.id !== task.id && t.assigned_to === task.assigned_to && t.status !== 'done'
  );

  const workloadMental = Math.min(70, peers.reduce((s, t) => s + (MENTAL_BASE[t.priority] || 5), 0));
  const workloadPhys   = Math.min(70, peers.reduce((s, t) => s + (PHYS_BASE[t.priority]   || 3), 0));

  const mentalLeft = Math.max(0, 100 - workloadMental - mentalCost);
  const physLeft   = Math.max(0, 100 - workloadPhys   - physCost);

  return {
    mentalCost, physCost, mentalLeft, physLeft,
    overloaded: mentalLeft < 15 || physLeft < 15,
    atRisk:     mentalLeft < 30 || physLeft < 30,
    peerCount:  peers.length,
  };
}

function RingChart({ pct, color, label, size = 74 }) {
  const r      = (size - 12) / 2;
  const circ   = 2 * Math.PI * r;
  const offset = circ * (1 - Math.max(0, Math.min(100, pct)) / 100);
  const cx     = size / 2;
  const cy     = size / 2;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={9} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={9}
            strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: 14, fontWeight: 700, color }}>{Math.round(pct)}%</span>
        </div>
      </div>
      <span style={{ fontSize: 10, color: C.silver, textAlign: 'center', maxWidth: size, lineHeight: 1.3 }}>{label}</span>
    </div>
  );
}

function EnergyPanel({ task, allTasks, lang }) {
  const isNl = lang === 'nl';
  const e = calcEnergy(task, allTasks);
  if (!e) return (
    <div style={{ padding: '10px 0', color: C.silver, fontSize: 12 }}>
      {isNl ? 'Wijs een teamlid toe om de energieanalyse te zien.' : 'Assign a team member to see the energy analysis.'}
    </div>
  );

  const mentalColor  = e.mentalCost > 60 ? '#C05050' : e.mentalCost > 35 ? '#C8A84B' : '#4AC87A';
  const physColor    = e.physCost > 60   ? '#C05050' : e.physCost > 35   ? '#C8A84B' : '#4AC87A';
  const leftMColor   = e.mentalLeft < 15 ? '#C05050' : e.mentalLeft < 30 ? '#C8A84B' : '#4AC87A';
  const leftPColor   = e.physLeft < 15   ? '#C05050' : e.physLeft < 30   ? '#C8A84B' : '#4AC87A';

  return (
    <div style={{
      marginTop: 10,
      padding: '12px 14px',
      background: '#12121a',
      borderRadius: 10,
      border: '1px solid rgba(255,255,255,0.08)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <span style={{ fontSize: 14 }}>⚡</span>
        <span style={{ color: C.white, fontSize: 12, fontWeight: 700 }}>
          {isNl ? 'Energieanalyse — Coach' : 'Energy Analysis — Coach'}
        </span>
        {e.peerCount > 0 && (
          <span style={{ color: C.silver2, fontSize: 10, marginLeft: 'auto' }}>
            {isNl ? `+ ${e.peerCount} actieve taak${e.peerCount !== 1 ? 'en' : ''}` : `+ ${e.peerCount} active task${e.peerCount !== 1 ? 's' : ''}`}
          </span>
        )}
      </div>

      {/* Overload warning */}
      {e.overloaded && (
        <div style={{
          background: '#C0505018',
          border: '1px solid #C0505044',
          borderRadius: 8,
          padding: '8px 12px',
          marginBottom: 12,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
        }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>🚨</span>
          <span style={{ fontSize: 12, color: '#C05050', lineHeight: 1.5 }}>
            {isNl
              ? 'Overbelastingsrisico! De energiereserves van deze persoon zijn kritiek laag. Overweeg taken te herverdelen of de deadline aan te passen.'
              : 'Overload risk! This person\'s energy reserves are critically low. Consider redistributing tasks or adjusting the deadline.'}
          </span>
        </div>
      )}
      {!e.overloaded && e.atRisk && (
        <div style={{
          background: '#C8A84B18',
          border: '1px solid #C8A84B44',
          borderRadius: 8,
          padding: '8px 12px',
          marginBottom: 12,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
        }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
          <span style={{ fontSize: 12, color: '#C8A84B', lineHeight: 1.5 }}>
            {isNl
              ? 'Hoge belasting. Let op de energiereserves van deze medewerker en plan voldoende hersteltijd in.'
              : 'High load. Monitor this employee\'s energy reserves and schedule sufficient recovery time.'}
          </span>
        </div>
      )}
      {!e.atRisk && (
        <div style={{
          background: '#4AC87A18',
          border: '1px solid #4AC87A44',
          borderRadius: 8,
          padding: '7px 12px',
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <span style={{ fontSize: 14 }}>✅</span>
          <span style={{ fontSize: 12, color: '#4AC87A' }}>
            {isNl ? 'Gezonde belasting — medewerker heeft voldoende energiereserves.' : 'Healthy load — employee has sufficient energy reserves.'}
          </span>
        </div>
      )}

      {/* Rings grid */}
      <div style={{ display: 'flex', justifyContent: 'space-around', gap: 8 }}>
        <RingChart pct={e.mentalCost}  color={mentalColor} label={isNl ? 'Mentale\nbelasting' : 'Mental\ncost'} />
        <RingChart pct={e.physCost}    color={physColor}   label={isNl ? 'Fysieke\nbelasting' : 'Physical\ncost'} />
        <RingChart pct={e.mentalLeft}  color={leftMColor}  label={isNl ? 'Mentale\nreserve' : 'Mental\nreserve'} />
        <RingChart pct={e.physLeft}    color={leftPColor}  label={isNl ? 'Fysieke\nreserve' : 'Physical\nreserve'} />
      </div>

      <div style={{ marginTop: 8, fontSize: 10, color: C.silver2, textAlign: 'center' }}>
        {isNl
          ? 'Berekend op basis van prioriteit, trefwoorden en huidige werkdruk'
          : 'Calculated from priority, keywords and current workload'}
      </div>
    </div>
  );
}

// ─── Constants ───────────────────────────────────────────────────────────────

const PRIORITY_COLORS = {
  high:   '#C05050',
  medium: '#C8A84B',
  low:    '#4AC87A',
  none:   '#667799',
};

const PRIORITY_LABELS = {
  high:   { nl: '🔴 Hoog',   en: '🔴 High' },
  medium: { nl: '🟡 Middel', en: '🟡 Medium' },
  low:    { nl: '🟢 Laag',   en: '🟢 Low' },
  none:   { nl: 'Geen',      en: 'None' },
};

const STATUS_COLORS = {
  todo:        '#667799',
  in_progress: '#5B9CF6',
  done:        '#4AC87A',
};

const STATUS_LABELS = {
  todo:        { nl: 'Te doen',     en: 'To Do' },
  in_progress: { nl: 'In uitvoering', en: 'In Progress' },
  done:        { nl: 'Klaar',       en: 'Done' },
};

const STATUS_CYCLE = { todo: 'in_progress', in_progress: 'done', done: 'todo' };

const EMPTY_FORM = {
  title: '',
  description: '',
  assigned_to: '',
  priority: 'none',
  due_date: '',
  status: 'todo',
};

// ─── Language helpers ─────────────────────────────────────────────────────────

let _isNl = true;
const t   = (nl, en) => _isNl ? nl : en;
const fmt = (d) => d ? new Date(d).toLocaleDateString(_isNl ? 'nl-NL' : 'en-GB') : '';

// ─── Style helpers ─────────────────────────────────────────────────────────────

const inputStyle = (extra = {}) => ({
  background: '#12121a',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  color: C.white,
  fontSize: 14,
  padding: '10px 14px',
  width: '100%',
  outline: 'none',
  ...extra,
});

const labelStyle = {
  color: C.silver,
  fontSize: 12,
  marginBottom: 4,
  display: 'block',
  fontWeight: 500,
};

const sheetPanel = {
  background: '#1e1e28',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 10,
  padding: '14px 16px',
  marginBottom: 12,
};

// ─── TaskCard ─────────────────────────────────────────────────────────────────

function TaskCard({ task, allTasks, canEdit, currentUserId, onCheckbox, onEdit, onDelete, lang }) {
  const [showEnergy, setShowEnergy] = useState(false);
  const isOverdue = task.due_date && task.status !== 'done' && new Date(task.due_date) < new Date();
  const statusColor = STATUS_COLORS[task.status] || STATUS_COLORS.todo;
  const canToggle = canEdit || task.assigned_to === currentUserId;

  const checkboxContent = () => {
    if (task.status === 'done') return <span style={{ color: '#4AC87A', fontSize: 15, fontWeight: 700 }}>✓</span>;
    if (task.status === 'in_progress') return <span style={{ color: '#5B9CF6', fontSize: 10, fontWeight: 700 }}>▶</span>;
    return null;
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      padding: '12px 16px',
      borderBottom: `1px solid ${_T.cardBorderBot}`,
      background: _T.bg3,
      marginBottom: 6,
      borderRadius: 10,
      animation: 'fadeUp 0.3s ease both',
    }}>
      {/* Checkbox circle */}
      <button
        onClick={() => canToggle && onCheckbox(task)}
        title={canToggle ? t('Status wijzigen', 'Change status') : ''}
        style={{
          flexShrink: 0,
          width: 28,
          height: 28,
          borderRadius: '50%',
          border: `2px solid ${statusColor}`,
          background: task.status === 'done' ? `${statusColor}22` : 'transparent',
          cursor: canToggle ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 2,
          transition: 'border-color 0.2s, background 0.2s',
          padding: 0,
        }}
      >
        {checkboxContent()}
      </button>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          <span style={{
            fontSize: 14,
            fontWeight: 600,
            color: C.white,
            textDecoration: task.status === 'done' ? 'line-through' : 'none',
            opacity: task.status === 'done' ? 0.55 : 1,
            wordBreak: 'break-word',
          }}>
            {task.title}
          </span>

          {/* Priority badge */}
          {task.priority && task.priority !== 'none' && (
            <span style={{
              fontSize: 11,
              fontWeight: 600,
              color: PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.none,
              background: `${PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.none}22`,
              border: `1px solid ${PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.none}44`,
              borderRadius: 6,
              padding: '1px 7px',
              whiteSpace: 'nowrap',
            }}>
              {PRIORITY_LABELS[task.priority]?.[lang === 'nl' ? 'nl' : 'en'] || task.priority}
            </span>
          )}
        </div>

        {task.description ? (
          <div style={{ fontSize: 12, color: C.silver, marginBottom: 5, wordBreak: 'break-word' }}>
            {task.description}
          </div>
        ) : null}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {task.assigned_to_name ? (
            <span style={{ fontSize: 12, color: C.silver2 }}>
              👤 {task.assigned_to_name}
            </span>
          ) : null}

          {task.due_date ? (
            <span style={{
              fontSize: 12,
              color: isOverdue ? '#C05050' : C.silver,
              fontWeight: isOverdue ? 600 : 400,
            }}>
              {isOverdue ? '⚠ ' : '📅 '}{fmt(task.due_date)}
            </span>
          ) : null}

          <span style={{
            fontSize: 11,
            color: statusColor,
            fontWeight: 500,
          }}>
            {STATUS_LABELS[task.status]?.[lang === 'nl' ? 'nl' : 'en'] || task.status}
          </span>

          {/* Energy coach toggle */}
          <button
            onClick={e => { e.stopPropagation(); setShowEnergy(v => !v); }}
            title={lang === 'nl' ? 'Energieanalyse' : 'Energy analysis'}
            style={{
              background: showEnergy ? '#5B9CF622' : 'transparent',
              border: `1px solid ${showEnergy ? '#5B9CF644' : 'rgba(255,255,255,0.12)'}`,
              borderRadius: 6,
              color: showEnergy ? '#5B9CF6' : C.silver2,
              fontSize: 11,
              fontWeight: 600,
              padding: '2px 7px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            ⚡ {lang === 'nl' ? 'Energie' : 'Energy'}
          </button>
        </div>

        {/* Energy panel */}
        {showEnergy && <EnergyPanel task={task} allTasks={allTasks} lang={lang} />}
      </div>

      {/* Actions (owner/admin/manager only) */}
      {canEdit && (
        <div style={{ display: 'flex', gap: 4, flexShrink: 0, marginTop: 2 }}>
          <button
            onClick={() => onEdit(task)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
              padding: '2px 4px',
              borderRadius: 4,
              color: C.silver,
              lineHeight: 1,
            }}
            title={t('Bewerken', 'Edit')}
          >
            ✏️
          </button>
          <button
            onClick={() => onDelete(task)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
              padding: '2px 4px',
              borderRadius: 4,
              color: '#C05050',
              lineHeight: 1,
            }}
            title={t('Verwijderen', 'Delete')}
          >
            🗑
          </button>
        </div>
      )}
    </div>
  );
}

// ─── FilterPills ──────────────────────────────────────────────────────────────

function FilterPills({ active, onChange, lang, currentUserId, tasks }) {
  const filters = [
    { id: 'all',         nl: 'Alle',         en: 'All' },
    { id: 'mine',        nl: 'Van mij',       en: 'Mine' },
    { id: 'todo',        nl: 'Te doen',       en: 'To Do' },
    { id: 'in_progress', nl: 'In uitvoering', en: 'In Progress' },
    { id: 'done',        nl: 'Klaar',         en: 'Done' },
  ];

  return (
    <div style={{
      display: 'flex',
      gap: 8,
      overflowX: 'auto',
      paddingBottom: 4,
      scrollbarWidth: 'none',
    }}>
      {filters.map(f => {
        const isActive = active === f.id;
        return (
          <button
            key={f.id}
            onClick={() => onChange(f.id)}
            style={{
              flexShrink: 0,
              padding: '6px 14px',
              borderRadius: 20,
              border: isActive ? `1px solid ${_T.accent}` : '1px solid rgba(255,255,255,0.12)',
              background: isActive ? `${_T.accent}22` : 'transparent',
              color: isActive ? _T.accent : C.silver,
              fontSize: 13,
              fontWeight: isActive ? 600 : 400,
              cursor: 'pointer',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
            }}
          >
            {lang === 'nl' ? f.nl : f.en}
          </button>
        );
      })}
    </div>
  );
}

// ─── TaskSheet (Add / Edit) ───────────────────────────────────────────────────

function TaskSheet({ task, members, businessId, onSave, onDelete, onClose, lang }) {
  const isEdit = !!task?.id;
  const [form, setForm] = useState(
    isEdit
      ? {
          title:       task.title || '',
          description: task.description || '',
          assigned_to: task.assigned_to || '',
          priority:    task.priority || 'none',
          due_date:    task.due_date ? task.due_date.slice(0, 10) : '',
          status:      task.status || 'todo',
        }
      : { ...EMPTY_FORM }
  );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError(t('Titel is verplicht.', 'Title is required.'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const assignedMember = form.assigned_to ? members.find(m => m.user_id === form.assigned_to) : null;
      const payload = {
        title:              form.title.trim(),
        description:        form.description.trim(),
        assigned_to:        form.assigned_to || null,
        assigned_to_name:   assignedMember ? (assignedMember.display_name || assignedMember.email) : null,
        priority:           form.priority,
        due_date:           form.due_date || null,
        status:             form.status,
      };
      const result = isEdit
        ? await updateTask(businessId, task.id, payload)
        : await createTask(businessId, payload);

      if (result?.error) throw new Error(result.error);
      onSave();
    } catch (e) {
      setError(t('Opslaan mislukt.', 'Save failed.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('Taak verwijderen?', 'Delete task?'))) return;
    setDeleting(true);
    try {
      await deleteTask(businessId, task.id);
      onDelete();
    } catch (e) {
      setError(t('Verwijderen mislukt.', 'Delete failed.'));
      setDeleting(false);
    }
  };

  const priorities = ['none', 'low', 'medium', 'high'];
  const statuses   = ['todo', 'in_progress', 'done'];

  return (
    <Portal>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.55)',
          zIndex: 1000,
          animation: 'fadeUp 0.2s ease both',
        }}
      />

      {/* Sheet */}
      <div style={{
        position: 'fixed',
        bottom: 0, left: 0, right: 0,
        background: '#14141a',
        borderRadius: '18px 18px 0 0',
        padding: '0 0 env(safe-area-inset-bottom)',
        zIndex: 1001,
        maxHeight: '92vh',
        overflowY: 'auto',
        animation: 'slideUp 0.28s cubic-bezier(0.32,0.72,0,1) both',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.6)',
      }}>
        {/* Handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.15)' }} />
        </div>

        <div style={{ padding: '8px 20px 28px' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.white }}>
              {isEdit ? t('Taak bewerken', 'Edit Task') : t('Nieuwe taak', 'New Task')}
            </h3>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: 'none',
                borderRadius: '50%',
                width: 30, height: 30,
                cursor: 'pointer',
                color: C.silver,
                fontSize: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              ✕
            </button>
          </div>

          {error && (
            <div style={{
              background: '#C0505022',
              border: '1px solid #C0505044',
              borderRadius: 8,
              padding: '8px 12px',
              color: '#C05050',
              fontSize: 13,
              marginBottom: 14,
            }}>
              {error}
            </div>
          )}

          {/* Title */}
          <div style={sheetPanel}>
            <label style={labelStyle}>{t('Titel', 'Title')} *</label>
            <input
              value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder={t('Taaknaam…', 'Task name…')}
              style={inputStyle()}
            />
          </div>

          {/* Description */}
          <div style={sheetPanel}>
            <label style={labelStyle}>{t('Omschrijving', 'Description')}</label>
            <textarea
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder={t('Optionele toelichting…', 'Optional details…')}
              rows={3}
              style={{ ...inputStyle(), resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>

          {/* Assignee */}
          <div style={sheetPanel}>
            <label style={labelStyle}>{t('Toegewezen aan', 'Assigned to')}</label>
            <select
              value={form.assigned_to}
              onChange={e => set('assigned_to', e.target.value)}
              style={inputStyle()}
            >
              <option value="">{t('— Niemand —', '— Nobody —')}</option>
              {members.map(m => (
                <option key={m.user_id} value={m.user_id}>
                  {m.display_name || m.email}
                </option>
              ))}
            </select>
          </div>

          {/* Priority */}
          <div style={sheetPanel}>
            <label style={labelStyle}>{t('Prioriteit', 'Priority')}</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {priorities.map(p => {
                const isActive = form.priority === p;
                const col = PRIORITY_COLORS[p];
                return (
                  <button
                    key={p}
                    onClick={() => set('priority', p)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 8,
                      border: isActive ? `1px solid ${col}` : '1px solid rgba(255,255,255,0.12)',
                      background: isActive ? `${col}22` : 'transparent',
                      color: isActive ? col : C.silver,
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 400,
                      cursor: 'pointer',
                    }}
                  >
                    {PRIORITY_LABELS[p]?.[lang === 'nl' ? 'nl' : 'en'] || p}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Due date + Status */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ ...sheetPanel, flex: 1, marginBottom: 12 }}>
              <label style={labelStyle}>{t('Deadline', 'Due date')}</label>
              <input
                type="date"
                value={form.due_date}
                onChange={e => set('due_date', e.target.value)}
                style={inputStyle({ colorScheme: 'dark' })}
              />
            </div>
            <div style={{ ...sheetPanel, flex: 1, marginBottom: 12 }}>
              <label style={labelStyle}>{t('Status', 'Status')}</label>
              <select
                value={form.status}
                onChange={e => set('status', e.target.value)}
                style={inputStyle()}
              >
                {statuses.map(s => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]?.[lang === 'nl' ? 'nl' : 'en'] || s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              width: '100%',
              padding: '13px',
              borderRadius: 12,
              border: 'none',
              background: saving ? `${_T.accent}88` : _T.accent,
              color: '#000',
              fontSize: 15,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              marginBottom: 10,
              transition: 'opacity 0.15s',
            }}
          >
            {saving ? t('Opslaan…', 'Saving…') : t('Opslaan', 'Save')}
          </button>

          {/* Delete (edit only) */}
          {isEdit && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 12,
                border: '1px solid #C0505044',
                background: '#C0505018',
                color: '#C05050',
                fontSize: 14,
                fontWeight: 600,
                cursor: deleting ? 'not-allowed' : 'pointer',
              }}
            >
              {deleting ? t('Verwijderen…', 'Deleting…') : t('Taak verwijderen', 'Delete task')}
            </button>
          )}
        </div>
      </div>
    </Portal>
  );
}

// ─── BusinessTasksScreen ──────────────────────────────────────────────────────

export default function BusinessTasksScreen({ lang, businessId, currentUserId, role }) {
  _isNl = lang === 'nl';

  const [tasks,   setTasks]   = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('all');
  const [sheet,   setSheet]   = useState(null); // null | 'new' | task object

  const canEdit = ['owner', 'admin', 'manager'].includes(role);

  // ── Load ────────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [taskRes, memberRes] = await Promise.all([
        getTasks(businessId),
        getMembers(businessId),
      ]);
      setTasks(Array.isArray(taskRes) ? taskRes : (taskRes?.tasks || []));
      setMembers(Array.isArray(memberRes) ? memberRes : (memberRes?.members || []));
    } catch (e) {
      // silently fail — list stays empty
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  // ── Filter ──────────────────────────────────────────────────────────────────

  const filtered = tasks.filter(task => {
    if (filter === 'mine')        return task.assigned_to === currentUserId;
    if (filter === 'todo')        return task.status === 'todo';
    if (filter === 'in_progress') return task.status === 'in_progress';
    if (filter === 'done')        return task.status === 'done';
    return true;
  });

  // ── Checkbox cycle ──────────────────────────────────────────────────────────

  const handleCheckbox = useCallback(async (task) => {
    const canToggle = canEdit || task.assigned_to === currentUserId;
    if (!canToggle) return;
    const nextStatus = STATUS_CYCLE[task.status] || 'todo';

    // Optimistic update
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: nextStatus } : t));

    try {
      await updateTask(businessId, task.id, { status: nextStatus });
    } catch (e) {
      // Rollback on failure
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: task.status } : t));
    }
  }, [businessId, canEdit, currentUserId]);

  // ── Sheet handlers ──────────────────────────────────────────────────────────

  const handleSheetSave = async () => {
    setSheet(null);
    await load();
  };

  const handleSheetDelete = async () => {
    setSheet(null);
    await load();
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{CSS}</style>
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        ::-webkit-scrollbar { width: 3px; height: 3px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 2px; }
      `}</style>

      <div style={{
        minHeight: '100%',
        background: _T.bg,
        padding: '0 0 40px',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 20px 12px',
          gap: 12,
        }}>
          <h2 style={{
            margin: 0,
            fontSize: 20,
            fontWeight: 700,
            color: C.white,
          }}>
            {t('Taken', 'Tasks')}
          </h2>

          {canEdit && (
            <button
              onClick={() => setSheet('new')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 16px',
                borderRadius: 10,
                border: `1px solid ${_T.accent}44`,
                background: `${_T.accent}18`,
                color: _T.accent,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              + {t('Taak', 'Task')}
            </button>
          )}
        </div>

        {/* Filter pills */}
        <div style={{ padding: '0 20px 16px' }}>
          <FilterPills
            active={filter}
            onChange={setFilter}
            lang={lang}
            currentUserId={currentUserId}
            tasks={tasks}
          />
        </div>

        {/* Content */}
        <div style={{ padding: '0 16px' }}>
          {loading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 0',
              color: C.silver,
              fontSize: 14,
            }}>
              <span style={{
                display: 'inline-block',
                width: 22,
                height: 22,
                border: `2px solid ${_T.accent}44`,
                borderTopColor: _T.accent,
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                marginRight: 10,
              }} />
              {t('Laden…', 'Loading…')}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 20px',
              color: C.silver,
              textAlign: 'center',
              gap: 12,
            }}>
              <span style={{ fontSize: 40 }}>📋</span>
              <span style={{ fontSize: 15, fontWeight: 600, color: C.silver2 }}>
                {t('Geen taken gevonden', 'No tasks found')}
              </span>
              <span style={{ fontSize: 13, color: C.silver }}>
                {filter === 'all'
                  ? t('Maak een nieuwe taak aan om te beginnen.', 'Create a new task to get started.')
                  : t('Er zijn geen taken met dit filter.', 'No tasks match this filter.')}
              </span>
            </div>
          ) : (
            filtered.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                allTasks={tasks}
                canEdit={canEdit}
                currentUserId={currentUserId}
                onCheckbox={handleCheckbox}
                onEdit={t => setSheet(t)}
                onDelete={async (t) => {
                  if (!window.confirm(lang === 'nl' ? 'Taak verwijderen?' : 'Delete task?')) return;
                  try {
                    await deleteTask(businessId, t.id);
                    await load();
                  } catch (e) { /* ignore */ }
                }}
                lang={lang}
              />
            ))
          )}
        </div>
      </div>

      {/* Add / Edit sheet */}
      {sheet !== null && (
        <TaskSheet
          task={sheet === 'new' ? null : sheet}
          members={members}
          businessId={businessId}
          onSave={handleSheetSave}
          onDelete={handleSheetDelete}
          onClose={() => setSheet(null)}
          lang={lang}
        />
      )}
    </>
  );
}
